import { useEffect, useState, useRef } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ENTER_GAME, EXIT_GAME } from "../../graphql/games";
import useMainStore from "../../store/mainState";
import { GET_USER } from "../../graphql/auth";
import GLoader from "../../components/ui/base/GLoader";
import GButton from "../../components/ui/base/GButton";

declare global {
    interface Window {
        SlotServSportsbook?: {
            ApiClient: new (config: { target: string }) => {
                setCustomCSS: (config: { url: string }) => void;
                enableCustomCSS: () => void;
            };
        };
    }
}

// Public sportsbook URL shown to logged-out (demo) users. No enterGame session
// is created for these users; the login/register buttons inside the iframe open
// the auth modals via the postMessage handler.
const DEMO_SPORTSBOOK_URL = "https://sport-iframe.dfkfkkk6l.com/en/";

const GGSport = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [enterGame] = useMutation(ENTER_GAME);
    const [exitGame] = useMutation(EXIT_GAME);
    const userId = localStorage.getItem("userId");
    const authToken = localStorage.getItem("authToken");

    const { loading: userLoading, error: userError, data: userData, refetch: refetchUser } = useQuery(GET_USER, {
        variables: { id: Number(userId) },
        skip: !userId,
        fetchPolicy: 'cache-and-network'
    });
    const { user, setIsSignInOpen, setIsSignUpOpen } = useMainStore();
    const isAuthenticated = !!(user || (userId && authToken));
    const hasEnteredGame = useRef(false);
    const apiClientRef = useRef<any>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [ggBetData, setGgBetData] = useState<any>();
    const [enterError, setEnterError] = useState<string | null>(null);

    useEffect(() => {
        // Logged-out (demo) users get the public sportsbook iframe directly —
        // no enterGame session is created. Login/register buttons inside the
        // iframe open the auth modals via the postMessage handler below. The
        // entry guard is reset so logging in swaps the demo iframe for a real
        // session.
        if (!isAuthenticated) {
            hasEnteredGame.current = false;
            setGgBetData({ launchUrl: DEMO_SPORTSBOOK_URL });
            setIsLoading(true);
            return;
        }
        if (hasEnteredGame.current) return;
        hasEnteredGame.current = true;
        setIsLoading(true);

        (async () => {
            try {
                const response = await enterGame({
                    variables: {
                        input: {
                            userId: userId ? Number(userId) : undefined,
                            gameId: 'sport',
                            redirectUrl: window.location.origin,
                            providerId: 11
                        },
                    },
                });
                const data = response?.data?.enterGame;
                const gqlError = response?.errors?.[0]?.message;
                if (gqlError || !data?.launchUrl) {
                    hasEnteredGame.current = false;
                    setIsLoading(false);
                    setEnterError(gqlError || 'Sportsbook is unavailable');
                    return;
                }
                setGgBetData(data);
            } catch (err: any) {
                hasEnteredGame.current = false;
                setIsLoading(false);
                const message =
                    err?.graphQLErrors?.[0]?.message ||
                    err?.networkError?.result?.errors?.[0]?.message ||
                    err?.message ||
                    'Sportsbook is unavailable';
                setEnterError(message);
            }
        })();
    }, [enterGame, userId, user?.id, isAuthenticated]);
    const [apiLoaded, setApiLoaded] = useState(false);

    // Handle login/register button clicks coming from inside the sportsbook
    // iframe. In demo mode the sportsbook is shown to logged-out users, so when
    // they click login/register we surface the corresponding auth modal.
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const data = event.data;
            console.log({data});

            if (!data || typeof data !== 'object') {
                return;
            }

            if (data.type === 'login.click') {
                setIsSignInOpen(true);
            }

            if (data.type === 'register.click') {
                setIsSignUpOpen(true);
            }

            if (data.type === 'deposit.click') {
                navigate('/payment');
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [setIsSignInOpen, setIsSignUpOpen, navigate]);


    useEffect(() => {
        // Define callback BEFORE injecting the script

        //@ts-ignore
        window.ON_SPORTSBOOK_API_LOADED = () => {
            //@ts-ignore
            const api = window.SlotServSportsBook.SBApi.createClient();
            apiClientRef.current = api;
            setApiLoaded(true);
        };

        const script = document.createElement("script");
        script.src = "https://sportsbook.slotserv.com/sb/sbapi/v1/sportsbook-api.js";
        script.async = true;
        document.head.appendChild(script);

        return () => { document.head.removeChild(script); };
    }, []);



    // Apply custom CSS branding once the SlotServ API client is ready and the
    // sportsbook has launched. This is the supported, cross-origin-safe path —
    // the API client relays the CSS to the sportsbook for us.
    useEffect(() => {
        if (!apiLoaded || !apiClientRef.current || !ggBetData?.launchUrl) return;
        try {
            apiClientRef.current.setCustomCSS({ url: 'https://flow888.app/downloads/moonlight.css' });
            apiClientRef.current.enableCustomCSS();
        } catch (err) {
            console.error('Failed to apply custom CSS:', err);
        }
    }, [apiLoaded, ggBetData?.launchUrl]);

    useEffect(() => {
        return () => {
            if (ggBetData?.sessionToken) {
                exitGame({
                    variables: {
                        sessionToken: ggBetData?.sessionToken,
                    },
                }).then(() => {
                    setGgBetData(null);
                    setIsLoading(false);
                }).catch(() => {
                    setIsLoading(false);
                });
            }
        };
    }, [ggBetData?.sessionToken]);

    return (
        <div className="w-full h-[100vh] relative">
            {enterError ? (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#0a0e1a] px-[20px]">
                    <div className="w-[80px] h-[80px] rounded-full bg-white/[0.04] border border-white/[0.06] flex items-center justify-center mb-[20px]">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                        </svg>
                    </div>
                    <h2 className="text-white text-[20px] font-bold mb-[8px] text-center">{t('ggSport.maintenance')}</h2>
                    <p className="text-white/40 text-[14px] text-center max-w-[360px] mb-[24px]">
                        {t('ggSport.maintenanceDesc')}
                    </p>
                    <div className="flex gap-[12px]">
                        <GButton className="px-[28px] py-[12px]" onClick={() => navigate(-1)} isGradient={true}>
                            {t('ggSport.goBack')}
                        </GButton>
                        <GButton className="px-[28px] py-[12px]" onClick={() => navigate('/')} variant="outline">
                            {t('ggSport.home')}
                        </GButton>
                    </div>
                </div>
            ) : (
                <>
                    {isLoading && <GLoader />}
                    {ggBetData?.launchUrl && (
                        <iframe
                            id="sportsbook-root"
                            src={ggBetData.launchUrl}
                            width="100%"
                            height="100%"
                            onLoad={() => setIsLoading(false)}
                            className="w-full h-full overflow-hidden overflow-y-auto border-0"
                            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-top-navigation allow-popups-to-escape-sandbox"
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default GGSport;
